import { ActionReducerMapBuilder, AsyncThunk, PayloadAction, createSlice } from "@reduxjs/toolkit";
import {
  BiaConfig,
  BiaImage,
  BiaPayloadItem,
  BiaPlan,
  BiaPlanVersionInfo,
  BiaRejection,
  BiaSession,
  isBiaTerminal,
} from "../../interfaces/BulkImageAnalysisV2Object";
import {
  cancelBiaSession,
  confirmBiaPlan,
  confirmBiaUploads,
  createBiaProducts,
  createBiaSession,
  deleteBiaImage,
  exportBiaPlan,
  fetchBiaConfig,
  fetchBiaImages,
  fetchBiaPayload,
  fetchBiaPlan,
  fetchBiaPlanVersions,
  fetchBiaSession,
  fetchBiaSessions,
  finishBiaSession,
  patchBiaImage,
  restoreBiaPlan,
  retryBiaGenerate,
  reviseBiaPlan,
  saveBiaPlan,
  startBiaGenerate,
  startBiaPlan,
  unconfirmBiaPlan,
  updateBiaSession,
} from "../../services/thunks/BulkImageAnalysisV2Thunk";
import {
  BIA_CONFIG_KEY,
  BIA_CREATE_KEY,
  BiaAction,
  biaActionKey,
  biaCreateProductsKey,
  biaExportKey,
  biaImageActionKey,
  biaImagesKey,
  biaListKey,
  biaPayloadKey,
  biaPlanKey,
  biaSessionKey,
  biaVersionsKey,
} from "./BulkImageAnalysisV2Keys";
import { resetAllState } from "./resetAllState";
import { AsyncStatusState, createAsyncStatusState } from "./shared/registerAsync";

export * from "./BulkImageAnalysisV2Keys";

/**
 * State for Smart photos (bulk image analysis v2) — a CACHE of the server
 * session, never the source of truth (a reload resumes from the server).
 * Nothing here is persisted to session / local storage.
 *
 * Shape:
 *  - `sessionsById` — every session seen (the one on screen is refreshed by
 *    polling while a job runs);
 *  - `activeByBusiness` — the active session found for a business by the
 *    resume lookup (`null` = looked, none; absent = not looked yet);
 *  - `imagesBySession` — photos keyed by imageId (confirm results and the full
 *    list merge into the same map);
 *  - `plansBySession[sessionId][version]` — plans fetched so far; the one to
 *    show is `session.currentPlanVersion`;
 *  - `versionsBySession`, `payloadBySession` — version list and the generated
 *    products;
 *  - loadingMap / errorMap / successMap as everywhere.
 */

export interface BiaImagesEntry {
  byId: Record<string, BiaImage>;
  /** True once a full GET /images has landed (confirm results alone do not count). */
  hasLoaded: boolean;
}

export interface BiaPayloadEntry {
  items: BiaPayloadItem[];
  total: number;
  hasLoaded: boolean;
}

export interface BulkImageAnalysisV2State extends AsyncStatusState {
  /** Limits and the feature switch; null until GET /config answered. */
  config: BiaConfig | null;
  sessionsById: Record<string, BiaSession>;
  /** businessId -> id of its active session; null when there is none. */
  activeByBusiness: Record<string, string | null>;
  imagesBySession: Record<string, BiaImagesEntry>;
  plansBySession: Record<string, Record<number, BiaPlan>>;
  versionsBySession: Record<string, BiaPlanVersionInfo[]>;
  payloadBySession: Record<string, BiaPayloadEntry>;
}

const initialState = (): BulkImageAnalysisV2State => ({
  config: null,
  sessionsById: {},
  activeByBusiness: {},
  imagesBySession: {},
  plansBySession: {},
  versionsBySession: {},
  payloadBySession: {},
  ...createAsyncStatusState(),
});

// ── Reducer helpers ──────────────────────────────────────────────────────────

function upsertSession(state: BulkImageAnalysisV2State, session: BiaSession): void {
  if (!session.sessionId) return;
  state.sessionsById[session.sessionId] = session;
  if (session.businessId) {
    if (isBiaTerminal(session.status)) {
      if (state.activeByBusiness[session.businessId] === session.sessionId) {
        state.activeByBusiness[session.businessId] = null;
      }
    } else {
      state.activeByBusiness[session.businessId] = session.sessionId;
    }
  }
}

function imagesEntry(state: BulkImageAnalysisV2State, sessionId: string): BiaImagesEntry {
  if (!state.imagesBySession[sessionId]) {
    state.imagesBySession[sessionId] = { byId: {}, hasLoaded: false };
  }
  return state.imagesBySession[sessionId];
}

function upsertImages(state: BulkImageAnalysisV2State, sessionId: string, images: BiaImage[]): void {
  const entry = imagesEntry(state, sessionId);
  images.forEach((image) => {
    if (!image.imageId) return;
    if (image.status === "DELETED") {
      delete entry.byId[image.imageId];
    } else {
      entry.byId[image.imageId] = image;
    }
  });
}

type Rejection = BiaRejection | undefined;

interface RegisterConfig<Returned, Arg> {
  key: string | ((arg: Arg) => string);
  loading?: string;
  /** Skip loading / error bookkeeping entirely (silent polling, background queues). */
  silent?: (arg: Arg) => boolean;
  onPending?: (state: BulkImageAnalysisV2State, arg: Arg) => void;
  onFulfilled?: (state: BulkImageAnalysisV2State, payload: Returned, arg: Arg) => void;
  /** Return true when the rejection was handled as a state, not a failure. */
  onRejected?: (state: BulkImageAnalysisV2State, rejection: BiaRejection, arg: Arg) => boolean | void;
}

const FALLBACK_REJECTION: BiaRejection = {
  message: "Something went wrong. Please try again.",
  code: "CLIENT_ERROR",
  httpStatus: 0,
  retryable: true,
  details: null,
};

/**
 * registerAsync's counterpart for thunks that reject with a BiaRejection: same
 * loadingMap / errorMap / successMap bookkeeping.
 * (registerAsync pins `rejectValue: string`.)
 */
function register<Returned, Arg>(
  builder: ActionReducerMapBuilder<BulkImageAnalysisV2State>,
  thunk: AsyncThunk<Returned, Arg, { rejectValue: BiaRejection }>,
  config: RegisterConfig<Returned, Arg>
): void {
  const keyOf = (arg: Arg): string => (typeof config.key === "function" ? config.key(arg) : config.key);

  builder
    .addCase(thunk.pending, (state, action) => {
      const s = state as unknown as BulkImageAnalysisV2State;
      const arg = action.meta.arg as Arg;
      config.onPending?.(s, arg);
      if (config.silent?.(arg)) return;
      const key = keyOf(arg);
      s.loadingMap[key] = config.loading ?? "Loading...";
      s.errorMap[key] = null;
      s.successMap[key] = false;
    })
    .addCase(thunk.fulfilled, (state, action) => {
      const s = state as unknown as BulkImageAnalysisV2State;
      const arg = action.meta.arg as Arg;
      if (!config.silent?.(arg)) {
        const key = keyOf(arg);
        s.loadingMap[key] = null;
        s.successMap[key] = true;
        s.errorMap[key] = null;
      }
      config.onFulfilled?.(s, action.payload as Returned, arg);
    })
    .addCase(thunk.rejected, (state, action) => {
      const s = state as unknown as BulkImageAnalysisV2State;
      const arg = action.meta.arg as Arg;
      const rejection = ((action.payload as Rejection) ?? {
        ...FALLBACK_REJECTION,
        message: action.error?.message || FALLBACK_REJECTION.message,
      }) as BiaRejection;
      // A silent refresh that failed must not wipe what is on screen or flash
      // an error over it; the next tick tries again.
      if (config.silent?.(arg)) return;
      const key = keyOf(arg);
      s.loadingMap[key] = null;
      // A skipped (condition) or abandoned request is not a failure.
      if (action.meta.aborted || (action.meta as { condition?: boolean }).condition) return;
      const handled = config.onRejected?.(s, rejection, arg);
      if (!handled) {
        s.errorMap[key] = rejection.message;
      }
    });
}

// ── Slice ────────────────────────────────────────────────────────────────────

const bulkImageAnalysisV2Slice = createSlice({
  name: "bulkImageAnalysisV2",
  initialState,
  reducers: {
    /** Photos the upload queue knows about (confirm results, failures that carry an image). */
    biaImagesUpserted(state, action: PayloadAction<{ sessionId: string; images: BiaImage[] }>) {
      upsertImages(state, action.payload.sessionId, action.payload.images);
    },
    /** Forgets a business's active session (after "Start a new analysis"). */
    biaActiveForgotten(state, action: PayloadAction<string>) {
      state.activeByBusiness[action.payload] = null;
    },
    /** Forgets the cached payload of a session (it is re-fetched on the next review). */
    biaPayloadCleared(state, action: PayloadAction<string>) {
      delete state.payloadBySession[action.payload];
    },
  },
  extraReducers: (builder) => {
    register(builder, fetchBiaConfig, {
      key: BIA_CONFIG_KEY,
      loading: "Loading limits...",
      onFulfilled: (state, config) => {
        state.config = config;
      },
    });

    // Resume lookup: the ACTIVE sessions of a business. The caller's own session
    // wins; for an admin the most recently updated one.
    register(builder, fetchBiaSessions, {
      key: ({ businessId }) => biaListKey(businessId),
      loading: "Looking for an analysis in progress...",
      onFulfilled: (state, rows, { businessId, filter }) => {
        if (filter === "ACTIVE") {
          const newest = [...rows].sort((a, b) => (b.updatedAt ?? "").localeCompare(a.updatedAt ?? ""))[0];
          state.activeByBusiness[businessId] = newest ? newest.sessionId : null;
        }
      },
    });

    register(builder, createBiaSession, {
      key: BIA_CREATE_KEY,
      loading: "Starting...",
      onFulfilled: (state, session) => upsertSession(state, session),
    });

    register(builder, fetchBiaSession, {
      key: ({ sessionId }) => biaSessionKey(sessionId),
      loading: "Loading analysis...",
      silent: ({ silent }) => !!silent,
      onFulfilled: (state, session) => upsertSession(state, session),
    });

    // Session actions — one "busy" key per action, the fresh session swapped in.
    const sessionAction = <Arg,>(
      thunk: AsyncThunk<BiaSession, Arg, { rejectValue: BiaRejection }>,
      action: BiaAction,
      sessionIdOf: (arg: Arg) => string,
      loading: string
    ) =>
      register(builder, thunk, {
        key: (arg) => biaActionKey(sessionIdOf(arg), action),
        loading,
        onFulfilled: (state, session) => upsertSession(state, session),
      });
    sessionAction(updateBiaSession, "update", (a) => a.sessionId, "Saving...");
    sessionAction(cancelBiaSession, "cancel", (id: string) => id, "Cancelling...");
    sessionAction(startBiaPlan, "plan", (id: string) => id, "Starting...");
    sessionAction(reviseBiaPlan, "revise", (a) => a.sessionId, "Sending your change...");
    sessionAction(saveBiaPlan, "save", (a) => a.sessionId, "Saving changes...");
    sessionAction(restoreBiaPlan, "restore", (a) => a.sessionId, "Restoring...");
    sessionAction(confirmBiaPlan, "confirm", (a) => a.sessionId, "Confirming...");
    sessionAction(unconfirmBiaPlan, "unconfirm", (a) => a.sessionId, "Undoing...");
    sessionAction(startBiaGenerate, "generate", (id: string) => id, "Starting...");
    sessionAction(retryBiaGenerate, "retry", (a) => a.sessionId, "Starting...");
    sessionAction(finishBiaSession, "finish", (id: string) => id, "Finishing...");

    // Photos.
    register(builder, fetchBiaImages, {
      key: (sessionId) => biaImagesKey(sessionId),
      loading: "Loading photos...",
      onFulfilled: (state, images, sessionId) => {
        const entry = imagesEntry(state, sessionId);
        // The server list is authoritative for everything it knows; photos only
        // seen through a confirm result stay until the next full load drops them.
        const next: Record<string, BiaImage> = {};
        images.forEach((image) => {
          if (image.status !== "DELETED") next[image.imageId] = image;
        });
        entry.byId = next;
        entry.hasLoaded = true;
      },
    });

    // The upload queue reports its own failures per file; no flags here.
    register(builder, confirmBiaUploads, {
      key: ({ sessionId }) => `biaConfirm_${sessionId}`,
      silent: () => true,
      onFulfilled: (state, result, { sessionId }) => upsertImages(state, sessionId, result.confirmed),
    });

    register(builder, patchBiaImage, {
      key: ({ imageId }) => biaImageActionKey(imageId),
      loading: "Saving...",
      onFulfilled: (state, result, { sessionId, imageId }) => {
        upsertSession(state, result.session);
        const entry = imagesEntry(state, sessionId);
        if (result.image) {
          entry.byId[imageId] = result.image;
          return;
        }
        const current = entry.byId[imageId];
        if (!current) return;
        entry.byId[imageId] = result.form.excluded
          ? {
              ...current,
              status: "EXCLUDED",
              exclusionReason: result.form.reason ?? "CURATOR",
              excludedBy: "CURATOR",
            }
          : { ...current, status: "UPLOADED", exclusionReason: null, excludedBy: null };
      },
    });

    register(builder, deleteBiaImage, {
      key: ({ imageId }) => biaImageActionKey(imageId),
      loading: "Removing...",
      onFulfilled: (state, result, { sessionId }) => {
        upsertSession(state, result.session);
        delete imagesEntry(state, sessionId).byId[result.imageId];
      },
    });

    // Plan.
    register(builder, fetchBiaPlan, {
      key: ({ sessionId }) => biaPlanKey(sessionId),
      loading: "Loading plan...",
      onFulfilled: (state, plan, { sessionId }) => {
        if (plan.version <= 0) return;
        if (!state.plansBySession[sessionId]) state.plansBySession[sessionId] = {};
        state.plansBySession[sessionId][plan.version] = plan;
      },
    });

    register(builder, fetchBiaPlanVersions, {
      key: (sessionId) => biaVersionsKey(sessionId),
      loading: "Loading versions...",
      onFulfilled: (state, versions, sessionId) => {
        state.versionsBySession[sessionId] = versions;
      },
    });

    register(builder, exportBiaPlan, {
      key: ({ sessionId }) => biaExportKey(sessionId),
      loading: "Preparing...",
    });

    // Payload.
    register(builder, fetchBiaPayload, {
      key: (sessionId) => biaPayloadKey(sessionId),
      loading: "Loading products...",
      onFulfilled: (state, result, sessionId) => {
        state.payloadBySession[sessionId] = { items: result.items, total: result.total, hasLoaded: true };
      },
    });

    // One chunk of the session-aware create: results mark cached items CREATED.
    register(builder, createBiaProducts, {
      key: ({ sessionId }) => biaCreateProductsKey(sessionId),
      loading: "Creating products...",
      onFulfilled: (state, result, { sessionId }) => {
        const session = state.sessionsById[sessionId];
        if (session && result.sessionStatus && result.sessionStatus !== "UNKNOWN") {
          session.status = result.sessionStatus;
        }
        const entry = state.payloadBySession[sessionId];
        if (!entry) return;
        const created = new Set(
          result.results
            .filter((r) => r.status === "CREATED" || r.status === "ALREADY_CREATED")
            .map((r) => r.clientItemId)
        );
        if (created.size === 0) return;
        entry.items = entry.items.map((item) =>
          created.has(item.clientItemId) ? { ...item, status: "CREATED" } : item
        );
      },
    });

    builder.addCase(resetAllState, () => initialState());
  },
});

export const {
  biaImagesUpserted,
  biaActiveForgotten,
  biaPayloadCleared,
} = bulkImageAnalysisV2Slice.actions;
export default bulkImageAnalysisV2Slice.reducer;

// ── Selectors ────────────────────────────────────────────────────────────────
// Plain functions of `{ bulkImageAnalysisV2 }` so they work with useSelector and
// tests without importing the store (RootState lives in UiDb.ts — importing it
// here would recreate the cycle the keys file exists to avoid). They return
// stable references from the state (no fresh arrays), safe for useSelector.

type StoreSlice = { bulkImageAnalysisV2: BulkImageAnalysisV2State };

export const selectBiaConfig = (state: StoreSlice): BiaConfig | null => state.bulkImageAnalysisV2.config;

export const selectBiaSession = (state: StoreSlice, sessionId: string | null | undefined): BiaSession | undefined =>
  sessionId ? state.bulkImageAnalysisV2.sessionsById[sessionId] : undefined;

/** `undefined` = not looked up yet; `null` = looked, no active session. */
export const selectBiaActiveSessionId = (state: StoreSlice, businessId: string | null | undefined): string | null | undefined =>
  businessId ? state.bulkImageAnalysisV2.activeByBusiness[businessId] : undefined;

export const selectBiaImagesEntry = (state: StoreSlice, sessionId: string | null | undefined): BiaImagesEntry | undefined =>
  sessionId ? state.bulkImageAnalysisV2.imagesBySession[sessionId] : undefined;

export const selectBiaPlanByVersion = (
  state: StoreSlice,
  sessionId: string | null | undefined,
  version: number | null | undefined
): BiaPlan | undefined =>
  sessionId && version ? state.bulkImageAnalysisV2.plansBySession[sessionId]?.[version] : undefined;

export const selectBiaVersions = (state: StoreSlice, sessionId: string | null | undefined): BiaPlanVersionInfo[] | undefined =>
  sessionId ? state.bulkImageAnalysisV2.versionsBySession[sessionId] : undefined;

export const selectBiaPayloadEntry = (state: StoreSlice, sessionId: string | null | undefined): BiaPayloadEntry | undefined =>
  sessionId ? state.bulkImageAnalysisV2.payloadBySession[sessionId] : undefined;

export const selectBiaLoading = (state: StoreSlice, key: string): boolean => !!state.bulkImageAnalysisV2.loadingMap[key];

/** Two primitive selectors rather than one object: an object would be new on every call and re-render every subscriber. */
export const selectBiaErrorMessage = (state: StoreSlice, key: string): string | null =>
  state.bulkImageAnalysisV2.errorMap[key] ?? null;
